import { ArrowRight, Loader2 } from 'lucide-react';

import Icon from 'components/icon/Icon';
import { Button } from 'components/ui/button';
import {
  useGetRiskDetail,
  useGetRiskTrends,
} from 'services/risk-checker/hooks';
import { RiskEngineName } from 'types/risk';
import useRiskCheckerStore from 'store/risk-checker';
import { RiskType } from 'services/risk-checker/type';
import RiskChart from './chart';
import Loader from 'components/Loader';
import { differenceInDays, format, parseISO } from 'date-fns';
import { cn } from 'lib/utils';
import { useTranslation } from 'react-i18next';
import RiskCheckerIntro from './RiskCheckerIntro';
import { useEffect, useState } from 'react';

export default function RiskCheckerPage() {
  const { t } = useTranslation();
  const { data, isFetching } = useGetRiskTrends();

  const [showIntro, setShowIntro] = useState(false);

  const getDaysPassed = (date: string | undefined) => {
    if (!date) {
      return 'NA';
    }

    const daysPassed = differenceInDays(new Date(), parseISO(date));
    return daysPassed === 0
      ? t('today')
      : daysPassed === 1
      ? t('yesterday')
      : `${daysPassed} ${t('days_ago')}`;
  };

  useEffect(() => {
    if (data && !data?.hypertension_risk && !data?.diabetes_risk) {
      setShowIntro(true);
    }
  }, [data]);

  if (isFetching) {
    return <Loader />;
  }

  if (showIntro) {
    return <RiskCheckerIntro onView={() => setShowIntro(false)} />;
  }

  return (
    <div className='max-w-5xl p-4 py-10 mx-auto space-y-4'>
      <div className='flex items-center justify-between'>
        <h1 className='text-lg font-semibold lg:text-xl'>
          {t('risk_analysis')}
        </h1>
        {data?.last_updated ? (
          <p className='text-sm font-medium underline text-tertiary underline-offset-2'>
            {t('last_updated')}: {getDaysPassed(data?.last_updated)}
          </p>
        ) : null}
      </div>

      <div className='grid items-start grid-cols-1 gap-4 md:grid-cols-2'>
        <RiskCard
          icon='hypertension'
          title={t('hypertension')}
          riskType='hypertension_risk'
          risk={data?.hypertension_risk}
        />
        <RiskCard
          icon='diabetes'
          title={t('diabetes')}
          riskType='diabetes_risk'
          risk={data?.diabetes_risk}
        />
      </div>
    </div>
  );
}

function RiskCard({
  icon,
  title,
  riskType,
  risk,
}: {
  icon: string;
  title: string;
  riskType: RiskEngineName;
  risk: RiskType | undefined;
}) {
  const { t } = useTranslation();
  const setRiskEngineName = useRiskCheckerStore((store) => store.setEngineName);
  const { mutate, isPending } = useGetRiskDetail();

  const onCheck = () => {
    setRiskEngineName(riskType);
    mutate({ risk_type: riskType });
  };

  if (risk) {
    const chartData = risk.trend.map((item) => ({
      label: format(item.label, 'yyyy-MM-dd'),
      value: item.probability,
    }));

    const hasTrend = risk.trend && risk?.trend.length > 1;

    return (
      <div className='border-2 border-[#E7E7E7] rounded-lg p-4 pt-3'>
        <div
          className={cn('space-y-2', hasTrend && 'pb-2 border-b-2')}
          style={{
            borderColor: hasTrend ? risk.category_color : 'transparent',
          }}
        >
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <Icon icon={icon} size={40} />
              <p className='font-semibold text-base md:text-lg lg:text-xl text-[#656D76]'>
                {title}
              </p>
            </div>
            {!hasTrend ? (
              <Button
                className='px-6 text-sm leading-tight h-9'
                onClick={onCheck}
                disabled={isPending}
              >
                <span className='pt-0.5'>{t('view_report')}</span>{' '}
                {isPending ? (
                  <Loader2 className='ml-2 size-4 animate-spin' />
                ) : (
                  <ArrowRight className='ml-2 size-4' />
                )}
              </Button>
            ) : null}
          </div>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-1'>
              <Icon
                icon={
                  riskType === 'diabetes_risk' ? 'x-circle' : 'check-circle'
                }
                size={20}
                color={risk.category_color}
              />
              <p className='font-semibold text-sm md:text-base pt-[2px]'>
                {risk.category}
              </p>
            </div>
            <p className='font-semibold text-sm md:text-base text-[#4B5363]'>
              {risk.probability}% {t('chances')}
            </p>
          </div>
        </div>

        {hasTrend ? (
          <div className='space-y-6'>
            <div className='w-2/3 pt-4 mx-auto'>
              <RiskChart chartData={chartData} />
            </div>
            <div className='flex justify-end'>
              <Button
                className='px-6 text-sm leading-tight h-9'
                onClick={onCheck}
                disabled={isPending}
              >
                <span className='pt-0.5'>{t('view_report')}</span>{' '}
                {isPending ? (
                  <Loader2 className='ml-2 size-4 animate-spin' />
                ) : (
                  <ArrowRight className='ml-2 size-4' />
                )}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className='w-full px-4 py-3 border-2 border-[#E7E7E7] rounded-lg'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <Icon icon={icon} size={40} />
          <p className='text-base font-semibold md:text-lg'>{title}</p>
        </div>

        <Button
          className='px-6 text-sm leading-tight h-9'
          onClick={onCheck}
          disabled={isPending}
        >
          <span className='pt-1'>{t('check_now')}</span>{' '}
          {isPending ? (
            <Loader2 className='ml-2 size-4 animate-spin' />
          ) : (
            <ArrowRight className='ml-2 size-4' />
          )}
        </Button>
      </div>
    </div>
  );
}
